/**
 * PageDetailSidebar — Sheet panel showing full per-page processing stats.
 *
 * Opens when a row in ProcessingStatsTab's pages table is clicked.
 * Fetches /processing-stats/pages/{pageNumber} and renders:
 *   1. Counts grid (chars, words, tables, images, sections, paragraphs, etc.)
 *   2. Coverage progress bars (text / table / image)
 *   3. Confidence radial charts + grade chips (if available)
 *   4. Content-type badges
 *   5. Page dimensions footer
 */

import { AlertCircle, Loader2, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { GradeChip, RadialScoreChart } from '@/components/ui/chart'
import { Progress } from '@/components/ui/progress'
import { Separator } from '@/components/ui/separator'
import { Sheet, SheetContent } from '@/components/ui/sheet'
import type { PageStatsDetail } from '@/services/api/lake'
import { lakeApi } from '@/services/api/lake'

// ─── helpers ─────────────────────────────────────────────────────────────────

function fmt(n: number | null | undefined, fallback = '—'): string {
  if (n == null) return fallback
  return n.toLocaleString()
}

function pctFmt(n: number | null | undefined): string {
  if (n == null) return '—'
  return `${n.toFixed(1)}%`
}

// ─── sub-components ──────────────────────────────────────────────────────────

function CountRow({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="flex items-center justify-between py-1">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-xs font-medium text-foreground tabular-nums">{value}</span>
    </div>
  )
}

function CoverageBar({ label, pct }: { label: string; pct: number | null | undefined }) {
  const value = pct ?? 0
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">{label}</span>
        <span className="text-xs text-muted-foreground tabular-nums">{pctFmt(pct)}</span>
      </div>
      <Progress value={Math.min(value, 100)} className="h-1.5" />
    </div>
  )
}

// ─── main component ───────────────────────────────────────────────────────────

export interface PageDetailSidebarProps {
  documentId: string
  pageNumber: number
  open: boolean
  onClose: () => void
}

export function PageDetailSidebar({
  documentId,
  pageNumber,
  open,
  onClose,
}: PageDetailSidebarProps) {
  const [data, setData] = useState<PageStatsDetail | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setLoading(true)
    setError(null)
    setData(null)
    lakeApi
      .getProcessingStatsPageDetail(documentId, pageNumber)
      .then(d => {
        if (!cancelled) setData(d)
      })
      .catch(() => {
        if (!cancelled) setError('Failed to load page details.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [documentId, pageNumber, open])

  const hasConfidence = data && (data.layout_score != null || data.ocr_score != null)
  const hasCoverage =
    data &&
    (data.text_coverage_pct != null ||
      data.table_coverage_pct != null ||
      data.image_coverage_pct != null)

  return (
    <Sheet
      open={open}
      onOpenChange={isOpen => {
        if (!isOpen) onClose()
      }}
    >
      <SheetContent
        side="right"
        className="w-full sm:w-[420px] sm:min-w-0 p-0 overflow-hidden flex flex-col"
        showCloseButton={false}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border flex-shrink-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-foreground">Page {pageNumber}</span>
            {data?.page_width_pts != null && data?.page_height_pts != null && (
              <span className="text-xs text-muted-foreground/70 font-mono">
                {data.page_width_pts.toFixed(0)} × {data.page_height_pts.toFixed(0)} pts
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-5">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          )}

          {error && !loading && (
            <div className="flex flex-col items-center gap-2 py-12 text-center">
              <AlertCircle className="h-6 w-6 text-muted-foreground/50" />
              <p className="text-xs text-muted-foreground">{error}</p>
            </div>
          )}

          {data && !loading && (
            <>
              {/* ── Confidence scores — top of page ── */}
              {hasConfidence && (
                <div>
                  <p className="text-[10px] text-muted-foreground/70 uppercase tracking-wider mb-3 font-medium">
                    Confidence Scores
                  </p>
                  <div className="flex gap-4 flex-wrap">
                    <RadialScoreChart score={data.layout_score} label="Layout" colorVar="chart-1" />
                    <RadialScoreChart score={data.ocr_score} label="OCR" colorVar="chart-2" />
                    {data.parse_score != null && (
                      <RadialScoreChart score={data.parse_score} label="Parse" colorVar="chart-3" />
                    )}
                    {data.table_score != null && (
                      <RadialScoreChart score={data.table_score} label="Table" colorVar="chart-4" />
                    )}
                  </div>
                </div>
              )}

              {/* ── Quality grades ── */}
              {(data.mean_grade || data.low_grade) && (
                <>
                  {hasConfidence && <Separator />}
                  <div>
                    <p className="text-[10px] text-muted-foreground/70 uppercase tracking-wider mb-1 font-medium">
                      Quality Grades
                    </p>
                    <p className="text-xs text-muted-foreground/70 mb-3">
                      Mean reflects overall page quality. Low marks the weakest-scoring component on
                      this page.
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      {data.mean_grade && (
                        <div className="bg-muted rounded-md px-3 py-2 flex flex-col gap-1.5">
                          <span className="text-[10px] text-muted-foreground uppercase tracking-wide">
                            Mean
                          </span>
                          <GradeChip grade={data.mean_grade} />
                        </div>
                      )}
                      {data.low_grade && (
                        <div className="bg-muted rounded-md px-3 py-2 flex flex-col gap-1.5">
                          <span className="text-[10px] text-muted-foreground uppercase tracking-wide">
                            Low
                          </span>
                          <GradeChip grade={data.low_grade} />
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}

              {/* ── Primary counts ── */}
              <Separator />
              <div>
                <p className="text-[10px] text-muted-foreground/70 uppercase tracking-wider mb-2 font-medium">
                  Content Counts
                </p>
                <div className="divide-y divide-border rounded-md border border-border px-3">
                  <CountRow label="Characters" value={fmt(data.char_count)} />
                  <CountRow label="Words" value={fmt(data.word_count)} />
                  <CountRow label="Paragraphs" value={fmt(data.paragraph_count)} />
                  <CountRow label="Titles" value={fmt(data.title_count)} />
                  <CountRow label="Section Headers" value={fmt(data.section_header_count)} />
                  <CountRow label="List Items" value={fmt(data.list_item_count)} />
                  <CountRow label="Tables" value={fmt(data.table_count)} />
                  {data.table_total_rows != null && (
                    <CountRow label="Table Rows (total)" value={fmt(data.table_total_rows)} />
                  )}
                  {data.table_total_cols != null && (
                    <CountRow label="Table Cols (total)" value={fmt(data.table_total_cols)} />
                  )}
                  <CountRow label="Images" value={fmt(data.image_count)} />
                  <CountRow label="Hyperlinks" value={fmt(data.hyperlink_count)} />
                  <CountRow label="Footnotes" value={fmt(data.footnote_count)} />
                  <CountRow label="Captions" value={fmt(data.caption_count)} />
                  <CountRow label="References" value={fmt(data.reference_count)} />
                  <CountRow label="Code Blocks" value={fmt(data.code_block_count)} />
                  <CountRow label="Formulas" value={fmt(data.formula_count)} />
                  {data.page_header_count != null && (
                    <CountRow label="Page Headers" value={fmt(data.page_header_count)} />
                  )}
                  {data.page_footer_count != null && (
                    <CountRow label="Page Footers" value={fmt(data.page_footer_count)} />
                  )}
                </div>
              </div>

              {/* ── Coverage ── */}
              {hasCoverage && (
                <>
                  <Separator />
                  <div>
                    <p className="text-[10px] text-muted-foreground/70 uppercase tracking-wider mb-3 font-medium">
                      Page Coverage
                    </p>
                    <div className="space-y-3">
                      <CoverageBar label="Text" pct={data.text_coverage_pct} />
                      <CoverageBar label="Tables" pct={data.table_coverage_pct} />
                      <CoverageBar label="Images" pct={data.image_coverage_pct} />
                    </div>
                  </div>
                </>
              )}

              {/* ── Content types ── */}
              {data.content_types.length > 0 && (
                <>
                  <Separator />
                  <div>
                    <p className="text-[10px] text-muted-foreground/70 uppercase tracking-wider mb-2 font-medium">
                      Content Types
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {data.content_types.map(ct => (
                        <Badge key={ct} variant="outline" className="text-[10px] capitalize">
                          {ct.replace(/_/g, ' ')}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}
